import React, { useMemo, PropsWithChildren, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { styled, Theme } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import OpSelector from './OpSelector';
import ExpressionPathBuilder from './ExpressionPathBuilder';

import _ from 'lodash';
import { LogOp, Expression, FilterPath, ConditionOp, ExpSide } from '../types';
import { FilterContext } from './FilterContainer';
import * as actions from '../actions';
import { SxProps } from '@mui/system';
import { isSimple } from '../utils';


export const Item = styled(Box)<{ ownerState: { disabled: boolean } }>(({ theme, ownerState }) => ({
    backgroundColor: !ownerState?.disabled ? (theme.palette.mode === 'dark' ? '#1A2027' : '#fff') : theme.palette.grey[200],
    ...theme.typography.body2,
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textAlign: 'center',
    color: !ownerState?.disabled ? theme.palette.text.secondary : theme.palette.grey[600],
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(1),
    cursor: 'default',
    position: 'relative',
    userSelect: 'none',
}));

const ControlElBase = styled(Item)(({ theme }) => ({
    padding: theme.spacing(1),
    display: 'block',
    position: 'absolute',
    width: theme.spacing(1),
    height: theme.spacing(1),
}));

const ControlElTopLeft = styled(ControlElBase)(({ theme }) => ({
    top: '-' + theme.spacing(2),
    left: '-' + theme.spacing(2),
}));

const ControlETopRight = styled(ControlElBase)(({ theme }) => ({
    top: '-' + theme.spacing(2),
    right: '-' + theme.spacing(2),
    border: 'none',
}));

const ControlElBottomLeft = styled(ControlElBase)(({ theme }) => ({
    bottom: '-' + theme.spacing(2),
    left: '-' + theme.spacing(2),
}));

const ControlElLeft = styled(ControlElBase)(({ theme }) => ({
    top: '50%',
    left: '-' + theme.spacing(2),
    transform: 'translateY(-100%)'
}));

const ControlElRight = styled(ControlElBase)(({ theme }) => ({
    top: '50%',
    right: '-' + theme.spacing(2),
    transform: 'translateY(-100%)'
}));

const ControlElBottomCenterLeft = styled(ControlElBase)(({ theme }) => ({
    bottom: '-' + theme.spacing(2),
    left: '33%',
    transform: 'translateX(-100%)'
}));

const ControlElBottomCenterRight = styled(ControlElBase)(({ theme }) => ({
    bottom: '-' + theme.spacing(2),
    left: '67%',
    transform: 'translateX(-100%)'
}));

const Check = styled(Checkbox)(({ theme }) => ({
    padding: 0,
    margin: 0,
    textAlign: 'center',
    color: theme.palette.text.secondary,
    position: 'absolute',
    top: '-' + theme.spacing(0.2),
    left: '-' + theme.spacing(0.2),
}));


//------------------------------------------------------------------------------------------------------------------
const ExpressionItem: React.FC<PropsWithChildren<{
    expression: Expression,
    path: FilterPath,
    toggleNegated: () => void,
    toggleSelected: () => void,
    remove: () => void,
    addAfter: () => void,
    addBefore: () => void,
    group: () => void,
    explode: () => void,
}>> = ({ expression, path, children, toggleNegated, toggleSelected, remove, addAfter, addBefore, group, explode }) => {
    const { disabled } = useContext(FilterContext)!;

    const sxProps: SxProps<Theme> = {
        fontSize: '12px',
        color: !disabled ? 'black' : 'grey',
        position: 'absolute',
        top: 2,
        left: 2
    };

    const parentExists = path.length > 0;

    return <Item ownerState={{ disabled }}>
        {
            parentExists && <ControlElTopLeft onClick={remove} ownerState={{ disabled }}>
                <CloseIcon fontSize='small' sx={sxProps} />
            </ControlElTopLeft>
        }
        <ControlElBottomLeft onClick={toggleNegated} ownerState={{ disabled }}>
            <PriorityHighIcon fontSize='small' sx={sxProps} />
        </ControlElBottomLeft>
        {
            parentExists && <ControlETopRight ownerState={{ disabled }}>
                <Check size='small' checked={expression.selected} onChange={toggleSelected} />
            </ControlETopRight>
        }
        {
            (parentExists || isSimple(expression)) && <ControlElLeft ownerState={{ disabled }}>
                <AddIcon fontSize='small' sx={sxProps} onClick={addBefore} />
            </ControlElLeft>
        }
        {
            (parentExists || isSimple(expression)) && <ControlElRight ownerState={{ disabled }}>
                <AddIcon fontSize='small' sx={sxProps} onClick={addAfter} />
            </ControlElRight>
        }
        {
            !isSimple(expression) && <ControlElBottomCenterLeft ownerState={{ disabled }}>
                <LinkIcon fontSize='small' sx={sxProps} onClick={group} />
            </ControlElBottomCenterLeft>
        }
        {
            (!isSimple(expression) && parentExists) && <ControlElBottomCenterRight ownerState={{ disabled }}>
                <LinkOffIcon fontSize='small' sx={sxProps} onClick={explode} />
            </ControlElBottomCenterRight>
        }
        {children}
    </Item>
}

//------------------------------------------------------------------------------------------------------------------
const FilterLogOp: React.FC<{ op: LogOp, setOp: (op: LogOp) => void }> = ({ op, setOp }) => {
    const { disabled } = useContext(FilterContext)!;

    const handleClick = () => {
        setOp(op === LogOp.and ? LogOp.or : LogOp.and);
    }

    return <Item onClick={handleClick} ownerState={{ disabled }}>{op === LogOp.or ? 'or' : 'and'}</Item>
}

//------------------------------------------------------------------------------------------------------------------
const FilterExpression: React.FC<{
    expression: Expression,
    path: FilterPath,
    hidden: boolean
}> = ({
    expression,
    path,
    hidden
}) => {
        const { items, ops, negated, selected } = expression;
        const dispatch = useDispatch();
        const { disabled } = useContext(FilterContext)!;
        const [opAnchorEl, setOpAnchorEl] = React.useState<HTMLDivElement | null>(null);

        const toggleNegated = () => !disabled ? dispatch(actions.negate(path, !negated)) : null;

        const toggleSelected = () => !disabled ? dispatch(actions.select(path, !selected)) : null;

        const remove = () => !disabled ? dispatch(actions.remove(path)) : null;

        const addAfter = () => !disabled ? dispatch(actions.addAfter(path)) : null;

        const addBefore = () => !disabled ? dispatch(actions.addBefore(path)) : null;

        const setOp = (index: number) => (op: LogOp) => !disabled ? dispatch(actions.setLogOp(path, (index - 1) / 2, op)) : null;

        const group = () => !disabled ? dispatch(actions.group(path)) : null;

        const explode = () => !disabled ? dispatch(actions.explode(path)) : null;

        const handleSelectOp = (event: React.MouseEvent<HTMLDivElement>) => !disabled ? setOpAnchorEl(event.currentTarget) : null;

        const handleOpSelected = (op: ConditionOp) => {
            if (expression.op != op) {
                dispatch(actions.setOp(path, op));
            }
        }

        const elems = useMemo<(Expression | LogOp)[]>(
            () =>
                items.reduce((acc, item, index) => {
                    acc.push(item);
                    if (index < ops.length) {
                        acc.push(ops[index]);
                    }
                    return acc;
                }, [] as (Expression | LogOp)[]),
            [items, ops]
        );

        const boxProps: SxProps<Theme> = {
            border: '1px solid grey',
            borderColor: theme => !disabled ? theme.palette.grey[600] : theme.palette.grey[400],
            minWidth: '3em',
            minHeight: '1em',
        };

        return <>
            {
                negated && <Item onClick={toggleNegated} ownerState={{ disabled }}>{'not'}</Item>
            }

            {
                <ExpressionItem
                    toggleNegated={toggleNegated}
                    toggleSelected={toggleSelected}
                    addAfter={addAfter}
                    addBefore={addBefore}
                    remove={remove}
                    group={group}
                    explode={explode}
                    expression={expression}
                    path={path}
                >
                    {
                        isSimple(expression) ? <Grid
                            container
                            direction='column'
                            alignItems='center'
                            justifyContent='space-around'
                        >
                            <ExpressionPathBuilder
                                expressionPath={expression.lhs}
                                filterPath={path}
                                side={ExpSide.left}
                                hidden={hidden}
                            />

                            {
                                expression.lhs.path != '' && <Box onClick={handleSelectOp} sx={{ pb: theme => theme.spacing(0.3), userSelect: 'none' }}>{expression.op.label}</Box>
                            }

                            <OpSelector
                                lhs={expression.lhs}
                                anchorEl={opAnchorEl}
                                onClose={() => setOpAnchorEl(null)}
                                selected={expression.op}
                                onSelect={handleOpSelected}
                            />

                            {
                                expression.lhs.path != '' && expression.op && (_.fill(new Array(expression.op.arity), 1)).map((el, index) =>
                                    <ExpressionPathBuilder
                                        expressionPath={expression.rhs[index]}
                                        rhsIndex={index}
                                        filterPath={path}
                                        side={ExpSide.right}
                                        key={index}
                                        hidden={hidden}
                                    />
                                )
                            }

                        </Grid> : elems.map((elem, index) => {
                            return index % 2 == 0 ?
                                <FilterExpression
                                    expression={elem as Expression}
                                    key={index}
                                    path={[...path, index / 2]}
                                    hidden={hidden}
                                />
                                :
                                <FilterLogOp op={elem as LogOp} key={index} setOp={setOp(index)} />
                        })
                    }
                </ExpressionItem>
            }
        </>;
    }

export default FilterExpression;
